/*
  La Sura - "Save this cake" (blocks/lasura-cake-save.liquid).

  Writes a design entry to the shortlist (localStorage "lasura:shortlist", shared with the
  collection page's hearts and assets/lasura-shortlist.js):
    { kind: 'design', id, handle, title, image, url, choices, summary, saved }
  url is the builder link with the choices, so opening it rebuilds the cake through the
  builder's prefill. The button is pressed while the cake on screen matches a saved design.
  While saved, the account link (data-save-account) carries the cake to the Saved cakes page
  as ?cake=handle~choices. Signed in, the account's list (sections/lasura-saved-cakes-data)
  is fetched on load and when the tab comes back into view; a cake already there reads
  "In your account" and links to the page itself.
  Event: design_save / design_remove { product, count }; design_account_click { product }.
*/
const KEY = 'lasura:shortlist';
const MAX = 20;

const read = () => {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};
const write = (list) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {}
  document.dispatchEvent(new CustomEvent('lasura:shortlist', { detail: list }));
};
const track = (name, detail) => {
  document.dispatchEvent(new CustomEvent(`lasura:${name}`, { detail }));
  try {
    window.Shopify?.analytics?.publish?.(name, detail);
  } catch {}
};

/* The builder's own parameters, in its order (size and height first). */
const current = () => (typeof window.lasuraCakeChoices === 'function' ? window.lasuraCakeChoices()?.toString() ?? null : null);
/* Compared sorted, so the same cake always matches whatever order it was saved in. */
const key = (choices) => new URLSearchParams([...new URLSearchParams(choices).entries()].sort(([a], [b]) => a.localeCompare(b))).toString();

const summary = (choices) =>
  [...new URLSearchParams(choices).values()]
    .map((v) => v.split('|').join(' and '))
    .filter(Boolean)
    .join(', ');

class LasuraCakeSave extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.button = this.querySelector('[data-save-button]');
    this.text = this.querySelector('[data-save-text]');
    this.status = this.querySelector('[data-save-status]');
    this.more = this.querySelector('[data-save-more]');
    this.account = this.querySelector('[data-save-account]');
    this.account?.addEventListener('click', () => track('design_account_click', { product: this.dataset.handle }));
    if (this.account && this.dataset.signedIn === 'true') {
      this.label = this.account.querySelector('a').textContent;
      this.loadAccount();
      document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && this.loadAccount());
    }
    this.button.addEventListener('click', () => this.toggle());
    let timer;
    const refresh = () => {
      clearTimeout(timer);
      timer = setTimeout(() => this.sync(), 250);
    };
    document.addEventListener('change', (event) => {
      if (event.target instanceof Element && event.target.closest('lasura-cake-builder, variant-picker')) refresh();
    });
    document.addEventListener('lasura:shortlist', refresh);
    /* The prefill sets choices from a link after load; check again once it has. */
    setTimeout(() => this.sync(), 600);
    this.hidden = false;
    this.sync();
  }

  async loadAccount() {
    try {
      const r = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=lasura-saved-cakes-data`);
      const data = new DOMParser().parseFromString(await r.text(), 'text/html').querySelector('[data-lasura-saved-cakes]');
      const list = JSON.parse(data?.textContent || '[]');
      this.inAccount = new Set(list.filter((x) => x.kind === 'design' && x.handle === this.dataset.handle).map((x) => key(x.choices)));
      this.sync();
    } catch {}
  }

  match(list = read(), choices = current()) {
    if (choices === null) return undefined;
    const k = key(choices);
    return list.find((x) => x.kind === 'design' && x.handle === this.dataset.handle && key(x.choices) === k);
  }

  sync() {
    const on = !!this.match();
    this.button.setAttribute('aria-pressed', String(on));
    this.text.textContent = on ? this.dataset.saved : this.dataset.save;
    if (this.account) {
      const choices = current();
      const kept = choices !== null && !!this.inAccount?.has(key(choices));
      this.account.hidden = !(on || kept);
      const a = this.account.querySelector('a');
      const url = new URL(this.dataset.accountUrl, location.origin);
      if (!kept && choices !== null) url.searchParams.set('cake', `${this.dataset.handle}~${choices}`);
      a.href = url.href;
      a.textContent = kept ? this.dataset.accountIn : this.label || a.textContent;
    }
  }

  toggle() {
    const choices = current();
    if (choices === null) return;
    const list = read();
    const found = this.match(list, choices);
    if (found) {
      write(list.filter((x) => x !== found));
      this.say(this.dataset.removedNote, false);
      track('design_remove', { product: this.dataset.handle, count: list.length - 1 });
    } else {
      const url = new URL(location.pathname, location.origin);
      const view = new URLSearchParams(location.search).get('view');
      if (view) url.searchParams.set('view', view);
      /* Appended: several of our toppers repeat their keys (Matt, 3 Nov). */
      new URLSearchParams(choices).forEach((value, key) => url.searchParams.append(key, value));
      list.push({
        kind: 'design',
        id: `d${Date.now().toString(36)}`,
        handle: this.dataset.handle,
        title: this.dataset.title,
        image: this.dataset.image,
        url: url.pathname + url.search,
        choices,
        summary: summary(choices),
        saved: new Date().toISOString().slice(0, 10),
      });
      /* Keep the newest; the oldest drops off past the limit. */
      write(list.slice(-MAX));
      this.say(this.dataset.savedNote, true);
      track('design_save', { product: this.dataset.handle, count: Math.min(list.length, MAX) });
      if (document.body.classList.contains('lasura-motion') && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.button.classList.remove('is-popping');
        void this.button.offsetWidth;
        this.button.classList.add('is-popping');
      }
    }
    this.sync();
  }

  say(text, more) {
    this.status.textContent = '';
    setTimeout(() => (this.status.textContent = text || ''), 50);
    if (this.more) this.more.hidden = !more;
  }
}

if (!customElements.get('lasura-cake-save')) customElements.define('lasura-cake-save', LasuraCakeSave);
